import {
  Alert,
  AlertIcon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { MenuItemLink } from "@components/ui-kit";
import { Trans } from "@lingui/macro";
import {
  BankDocumentTypeEnum,
  ExpenseStatusEnum,
  useCloneExpenseMutation,
  useDeleteExpenseMutation,
  useMarkExpenseAsPaidMutation,
} from "@src/__generated__/graphql";
import { ModalConfirm } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { PreviewAndDownloadCell } from "@src/components/widgets/TableCell";
import { onError } from "@src/utils/apollo";
import { can, withCan } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { type FunctionComponent, useContext } from "react";
import { PageExpensesCtx } from "../PageExpenses";
import type { TData } from "./TableExpenses";

export const PopoverRowActions: FunctionComponent<
  React.PropsWithChildren<{ id: string; paid?: boolean; model: TData }>
> = withCan(
  {
    do: [
      "expense_delete_own",
      "expense_delete_all",
      "expense_markAsPaid",
      "expense_update_own",
      "expense_update_all",
      "expense_create",
    ],
  },
  observer(function PopoverRowActions({ id, paid, model }) {
    const { manualPaymentModalStore, matchPaymentModalStore, workspaceStore } =
      useStore();
    const store = useContext(PageExpensesCtx);
    const router = useRouter();

    const [markExpenseAsPaid] = useMarkExpenseAsPaidMutation({
      variables: { id, date: toApiDate(manualPaymentModalStore.date.start) },
      onCompleted() {
        manualPaymentModalStore.disclosure.onClose();
        store.refetch?.();
      },
      ...onError(),
    });

    const [deleteExpense] = useDeleteExpenseMutation({
      variables: { id },
      ...onError(),
      onCompleted() {
        store.refetch?.();
      },
    });
    const deleteExpenseDialog = useDisclosure();

    const [clone] = useCloneExpenseMutation({
      variables: { id },
      onCompleted({ cloneExpense }) {
        if (cloneExpense) {
          router.push({
            pathname: "/expenses/edit",
            query: { id: cloneExpense.id },
          });
        }
      },
      ...onError(),
    });

    const handleMatchPayment = async () => {
      store.refetch?.();
    };

    return (
      <React.Fragment>
        <Menu>
          <MenuButton
            as={IconButton}
            colorScheme="grey"
            icon={<Icon name="dots-horizontal" />}
            variant="plain"
          />
          <MenuList>
            {(can("expense_update_all") || can("expense_update_own")) && (
              <MenuItemLink
                href={{ pathname: "/expenses/edit", query: { id } }}
              >
                <Trans>Edit</Trans>
              </MenuItemLink>
            )}
            {can(["expense_create"]) && (
              <MenuItem onClick={() => clone()}>
                <Trans>Duplicate</Trans>
              </MenuItem>
            )}
            {(can("expense_delete_own") || can("expense_delete_all")) && (
              <MenuItem onClick={deleteExpenseDialog.onOpen}>
                <Trans>Delete</Trans>
              </MenuItem>
            )}
            {!paid && can("expense_markAsPaid") && (
              <MenuItem
                onClick={() => {
                  manualPaymentModalStore.disclosure.onOpen({
                    input: {
                      currency_id: model.currency.id,
                      date: toApiDate(model.dueDate ?? new Date()),
                      amount: model.total_amount_with_vat_in_currency,
                      note: model._model.note ?? "",
                    },
                    onUpdate: async () => {
                      await markExpenseAsPaid();
                    },
                  });
                }}
              >
                <Trans>Mark as paid manually</Trans>
              </MenuItem>
            )}
            {paid && can("expense_markAsPaid") && (
              <MenuItem
                onClick={() => {
                  store.markAsUnpaidModal.onOpen({
                    id,
                    hasMatchedPayments: (model.accountMoves?.length ?? 0) > 0,
                  });
                }}
              >
                <Trans>Mark as unpaid</Trans>
              </MenuItem>
            )}
            {workspaceStore.settings?.enableBank &&
              model.status === ExpenseStatusEnum.Approved &&
              can("expense_markAsPaid", "bank_read_all") && (
                <MenuItem
                  onClick={() => {
                    matchPaymentModalStore.disclosure.onOpen({
                      selectedMoves: model.accountMoves ?? [],
                      document: {
                        id: model.id,
                        type: BankDocumentTypeEnum.Expense,
                        number: model.expense_no ?? "",
                        amount: model.total_amount_with_vat_in_currency,
                        currency: model.currency,
                        exchangeRate: model.exchange_rate,
                      },
                      onUpdate: handleMatchPayment,
                    });
                  }}
                >
                  <Trans>Match payment</Trans>
                </MenuItem>
              )}
            <PreviewAndDownloadCell value={model._model.expense_documents} />
          </MenuList>
        </Menu>
        <ModalConfirm
          {...deleteExpenseDialog}
          onConfirm={() => {
            deleteExpense();
          }}
        >
          <Text>
            <Trans>Are you sure you want to delete this expense?</Trans>
          </Text>
          {!!model.projectTimeBilling?.id && (
            <Alert mt="4" status="warning">
              <AlertIcon />
              <Text>
                <Trans>
                  This expense is linked to a locked Time Billing. Please update
                  the Time Billing after deleting.
                </Trans>
              </Text>
            </Alert>
          )}
        </ModalConfirm>
      </React.Fragment>
    );
  }),
);
